import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import type { WrappedResponse } from '../../api/types';
import { usePrefersReducedMotion } from '../../hooks/usePrefersReducedMotion';
import {
  BacklogSlide,
  BoardingSlide,
  DepartureSlide,
  DisagreementSlide,
  DropsSlide,
  EraSlide,
  FingerprintSlide,
  FormatsSlide,
  GemSlide,
  HarshnessSlide,
  HomeSlide,
  LongestSlide,
  LovedSlide,
  MapSlide,
  ObscuritySlide,
  OnBoardSlide,
  ScoreCardSlide,
  SkippedGenreSlide,
} from './Slides';
import { buildSlides } from './slides';
import type { NextPick } from './stub';
import { Hurry } from './SplitFlap';
import { StubTicket } from './StubTicket';
import { useTrain } from './useTrain';
import { World } from './World';
import type { Line } from '../lines';
import { Wordmark } from '../Wordmark';
import { RIDE_MS, SPAN } from './world';

interface Props {
  data: WrappedResponse;
  /**
   * The rider's lines in the order they will ride them. The train runs on into
   * the first one when the intro ends; the rest are shown as the route ahead.
   */
  lines: Line[];
  /** The first ticket on the first line, as the stub's next stop; null until the picks are in. */
  next: NextPick | null;
  /** Leave the intro: on to the first line, or (only if asked for) the central station. */
  onFinish: (to: 'line' | 'hub') => void;
}

/**
 * What happens on each ride between stops. The first is always plain, so the
 * rider sees the line before anything happens on it. The tunnel and the
 * passing train come early, once each, and the last ride runs into the
 * terminus under fireworks. Everything else alternates plain and express. A
 * short intro drops the early events before the closing one.
 */
type Ride = 'plain' | 'express' | 'tunnel' | 'passing' | 'fireworks';
function ridePlan(hops: number): Ride[] {
  const plan: Ride[] = Array.from({ length: hops }, () => 'plain');
  const taken = new Set<number>([0]);
  const put = (at: number, ride: Ride) => {
    if (at < 1 || at >= hops || taken.has(at)) return false;
    plan[at] = ride;
    taken.add(at);
    return true;
  };
  put(hops - 1, 'fireworks');
  put(1, 'tunnel');
  if (!put(3, 'passing')) put(2, 'passing');
  // The rides left over take turns, an express first.
  let fast = true;
  for (let i = 1; i < hops; i++) {
    if (taken.has(i)) continue;
    plan[i] = fast ? 'express' : 'plain';
    fast = !fast;
  }
  return plan;
}
/** The event rides want time: pull away, the event, come out of it, stop. */
const LONG_RIDE_MS: Partial<Record<Ride, number>> = { tunnel: 6200, passing: 5600, fireworks: 5600 };
/**
 * Where the tunnel is on the hop, as shares of the distance between the two
 * stops, and how fast it slides past (close to the glass, so faster than the
 * poles). Placed so the train pulls away in the open, is inside for the middle
 * of the ride, and is out again with time to brake before the platform.
 */
const TUNNEL = { from: 0.18, to: 0.64, k: 2.4 };
/** Where each firework bursts (share of the window across and down) and when (ms into the ride). */
const BURSTS = [
  { x: 22, y: 16, at: 700, size: 110 },
  { x: 66, y: 11, at: 1300, size: 140 },
  { x: 44, y: 21, at: 2000, size: 90 },
  { x: 81, y: 18, at: 2500, size: 120 },
  { x: 31, y: 9, at: 3000, size: 135 },
  { x: 57, y: 19, at: 3500, size: 100 },
];
/** How long a burst takes to bloom and fade; the last is done before the train stops. */
const BURST_MS = 1700;

/** A layer fixed to the line between two stops, sliding past with the camera. */
const onTheLine = (index: number, at: { from: number; to: number; k: number }): CSSProperties => ({
  left: `calc((${(index + at.from) * SPAN} - var(--cx)) * ${at.k}px + 50%)`,
  width: `${(at.to - at.from) * SPAN * at.k}px`,
});

/** How long the slide takes to clear before the train moves. */
const LEAVE_MS = 380;
/** The run out of the last stop: pulling away, then flat out until the tunnel wash takes over. */
const THROUGH_ACCEL_MS = 1500;
const THROUGH_MS = 2300;
const INTERACTIVE = 'button, a, input, textarea, select, [role="button"]';
/** How many of the rider's lines the route strip in the header shows. */
const ROUTE_AHEAD = 5;

type Phase = 'stopped' | 'leaving' | 'riding' | 'through';

/**
 * The departure: the rider starts on the platform at Tsugi Central and the
 * train pulls out, one stop per statistic. It owns its own ride, so the host
 * app only gives it the numbers and hears where the rider wants to go next.
 *
 * Self-contained on purpose: this folder, `intro.css` and the host's `.tsugi`
 * font and colour variables are all it needs.
 */
export function Intro({ data, lines, next: nextPick, onFinish }: Props) {
  const slides = useMemo(() => buildSlides(data), [data]);
  const reduced = usePrefersReducedMotion();
  const root = useRef<HTMLDivElement>(null);
  const train = useTrain(root);
  const [index, setIndex] = useState(0);
  const [phase, setPhase] = useState<Phase>('stopped');
  const alive = useRef(true);
  const timer = useRef(0);
  const say = useRef<HTMLElement>(null);
  /** The stop the rider tapped to finish, so its boards stop rattling and land. */
  const [hurried, setHurried] = useState(-1);

  const slide = slides[index]!;
  const plan = useMemo(() => ridePlan(slides.length - 1), [slides.length]);
  const ride = phase === 'riding' ? (plan[index] ?? 'plain') : null;
  const last = index === slides.length - 1;
  const first = lines[0];

  useLayoutEffect(() => train.place(index * SPAN), []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
      window.clearTimeout(timer.current);
    };
  }, []);

  const next = useCallback(() => {
    if (phase !== 'stopped' || last) return;
    const from = index;
    if (reduced) {
      train.place((from + 1) * SPAN);
      setIndex(from + 1);
      return;
    }
    // The slide clears, then the train moves; the next one appears once it has stopped.
    setPhase('leaving');
    timer.current = window.setTimeout(() => {
      setPhase('riding');
      const long = LONG_RIDE_MS[plan[from] ?? 'plain'];
      void train.travel(from * SPAN, (from + 1) * SPAN, long ?? RIDE_MS, long ? 'cruise' : 'smooth').then(() => {
        if (!alive.current) return;
        setIndex(from + 1);
        setPhase('stopped');
      });
    }, LEAVE_MS);
  }, [phase, last, index, reduced, train, plan]);

  // A tap while the stop is still coming in finishes it instead of leaving it
  // half read, and the next tap moves on. Every part of a stop is a CSS
  // animation timed off its `--d`, except the split-flap boards, which `Hurry`
  // lands. Without the Web Animations API a tap just moves on, as it used to.
  // The platform has nothing to read yet, so a tap there always departs.
  const advance = useCallback(() => {
    const el = say.current;
    const waiting = phase === 'stopped' && slide.kind !== 'boarding' && hurried !== index;
    if (waiting && el && typeof el.getAnimations === 'function') {
      const coming = el
        .getAnimations({ subtree: true })
        .filter((a) => a.playState !== 'finished' && a.effect?.getTiming().iterations !== Infinity);
      const flapping = [...el.querySelectorAll<HTMLElement>('.sf')].some((sf) => {
        const shown = [...sf.querySelectorAll('.sf-top:not(.sf-fold) i')].map((i) => i.textContent).join('');
        return shown.trim() !== (sf.getAttribute('aria-label') ?? '').toUpperCase().trim();
      });
      if (coming.length || flapping) {
        coming.forEach((a) => a.finish());
        setHurried(index);
        return;
      }
    }
    next();
  }, [phase, slide.kind, hurried, index, next]);

  // Off to the first line. The train does not stop at the central station on
  // the way: it pulls out of the last stop and keeps going, and the host's
  // tunnel wash and 次は card meet it at speed.
  const onward = useCallback(() => {
    if (!first) return onFinish('hub');
    if (reduced) return onFinish('line');
    if (phase === 'through') return;
    window.clearTimeout(timer.current);
    setPhase('leaving');
    timer.current = window.setTimeout(() => {
      setPhase('through');
      train.cruise(THROUGH_ACCEL_MS);
      timer.current = window.setTimeout(() => {
        if (alive.current) onFinish('line');
      }, THROUGH_MS);
    }, LEAVE_MS);
  }, [first, reduced, phase, train, onFinish]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.defaultPrevented || e.metaKey || e.ctrlKey || e.altKey) return;
      if ((e.target as Element | null)?.closest?.(INTERACTIVE)) return;
      if (e.key === 'ArrowRight' || e.key === ' ' || e.key === 'Enter') {
        e.preventDefault();
        advance();
      } else if (e.key === 'Escape') {
        onward();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [advance, onward]);

  const upcoming = slides[index + 1];

  return (
    <div
      className={`train phase-${phase}`}
      ref={root}
      style={
        {
          ['--end' as string]: (slides.length - 1) * SPAN,
          ...(first ? { ['--line' as string]: first.color } : {}),
        } as CSSProperties
      }
      onClick={(e) => {
        if (!(e.target as Element).closest(INTERACTIVE)) advance();
      }}
    >
      <header className="train-head">
        <div className="mark">
          次 <Wordmark />
        </div>
        <ol className="dots" aria-label={`Stop ${index + 1} of ${slides.length}`}>
          {slides.map((s, i) => (
            <li
              key={s.kind}
              className={i < index || phase === 'through' ? 'past' : i === index ? 'on' : ''}
            />
          ))}
          {/* The route goes on past the intro: the rider's own lines, in order. */}
          {lines.length > 0 && <li className="sep" aria-hidden="true" />}
          {lines.slice(0, ROUTE_AHEAD).map((l, i) => (
            <li
              key={l.slug}
              className={`ln${phase === 'through' && i === 0 ? ' on' : ''}`}
              style={{ ['--c' as string]: l.color } as CSSProperties}
              aria-hidden="true"
            />
          ))}
        </ol>
        <button className="leave" onClick={onward} disabled={phase === 'through'} aria-label="Skip intro">
          SKIP<span className="wide"> INTRO</span> ▶▶
        </button>
      </header>

      <div
        className={`glass${slide.kind === 'ticket' && phase !== 'riding' ? ' at-ticket' : ''}${ride ? ` ride-${ride}` : ''}`}
      >
        <World
          slides={slides}
          index={index}
          sky={ride === 'fireworks' && <Fireworks index={index} lines={lines} />}
        />
        {ride === 'tunnel' && (
          <div className="tunnel" aria-hidden="true" style={onTheLine(index, TUNNEL)} />
        )}
        {ride === 'passing' && (
          <div className="passing" aria-hidden="true">
            <i style={{ animationDuration: `${LONG_RIDE_MS.passing}ms` }} />
          </div>
        )}
        <div className="tint" aria-hidden="true" />
        <div className="refl" aria-hidden="true" />

        {phase !== 'riding' && phase !== 'through' && <div className="scrim" aria-hidden="true" />}

        {phase === 'riding' && upcoming && (
          <div className="ride-label" aria-hidden="true">
            <small>NEXT STOP</small>
            <b>{upcoming.sign.en}</b>
          </div>
        )}

        {phase === 'through' && first && (
          <div className="ride-label onward" aria-live="polite">
            <small>NEXT STOP ・ PLATFORM {first.id}</small>
            <b>
              <span lang="ja">{first.kanji}</span> {first.lineEn}
            </b>
            {lines.length > 1 && (
              <span className="then">
                THEN{' '}
                {lines.slice(1, 4).map((l) => (
                  <em key={l.slug} style={{ ['--c' as string]: l.color } as CSSProperties}>
                    {l.kanji}
                  </em>
                ))}
                {lines.length > 4 && <em className="more">+{lines.length - 4}</em>}
              </span>
            )}
          </div>
        )}

        {phase !== 'riding' && phase !== 'through' && (
          <main
            className={`say kind-${slide.kind}${phase === 'leaving' ? ' leaving' : ''}`}
            key={slide.kind}
            ref={say}
          >
            <Hurry.Provider value={hurried === index}>
              <div className="srOnly" aria-live="polite">
                {slide.sign.en}
              </div>
              {slide.kind === 'boarding' && (
                <BoardingSlide data={data} stops={slides.length - 1} onDepart={next} />
              )}
              {slide.kind === 'departure' && <DepartureSlide data={data} />}
              {slide.kind === 'harshness' && <HarshnessSlide data={data} />}
              {slide.kind === 'obscurity' && <ObscuritySlide data={data} />}
              {slide.kind === 'gem' && <GemSlide data={data} />}
              {slide.kind === 'disagreement' && <DisagreementSlide data={data} />}
              {slide.kind === 'home' && <HomeSlide data={data} lines={lines} />}
              {slide.kind === 'drops' && <DropsSlide data={data} />}
              {slide.kind === 'score_card' && <ScoreCardSlide data={data} />}
              {slide.kind === 'loved' && <LovedSlide data={data} />}
              {slide.kind === 'longest' && <LongestSlide data={data} />}
              {slide.kind === 'skipped' && <SkippedGenreSlide data={data} lines={lines} />}
              {slide.kind === 'formats' && <FormatsSlide data={data} />}
              {slide.kind === 'fingerprint' && <FingerprintSlide data={data} />}
              {slide.kind === 'on_board' && <OnBoardSlide data={data} />}
              {slide.kind === 'backlog' && <BacklogSlide data={data} />}
              {slide.kind === 'era' && <EraSlide data={data} />}
              {slide.kind === 'map' && <MapSlide data={data} />}
              {slide.kind === 'ticket' && (
                <StubTicket
                  data={data}
                  line={first ?? null}
                  lines={lines}
                  next={nextPick}
                  destination={first ? '乗車 ・ YOUR RECOMMENDATIONS' : '中央駅 ・ CENTRAL STATION'}
                  onEnter={onward}
                  onHub={first ? () => onFinish('hub') : null}
                />
              )}
            </Hurry.Provider>
          </main>
        )}
      </div>

      <footer className="train-foot">
        {!last && slide.kind !== 'boarding' && (
          <button className="next" onClick={advance} disabled={phase !== 'stopped'}>
            NEXT STOP ▶
          </button>
        )}
        {slide.kind === 'boarding' && <span className="hint">OR TAP ANYWHERE / PRESS →</span>}
      </footer>
    </div>
  );
}

/** Fireworks far off over the city, in the colours of the rider's own lines. */
function Fireworks({ index, lines }: { index: number; lines: Line[] }) {
  return (
    <div
      className="fireworks"
      aria-hidden="true"
      // They drift with the skyline, not the poles.
      style={{ transform: `translateX(calc((${index * SPAN} - var(--cx)) * 0.05px))` }}
    >
      {BURSTS.map((b, i) => (
        <i
          key={i}
          style={
            {
              left: `${b.x}%`,
              top: `${b.y}%`,
              width: b.size,
              height: b.size,
              animationDelay: `${b.at}ms`,
              animationDuration: `${BURST_MS}ms`,
              ['--fw' as string]: lines[i % Math.max(1, lines.length)]?.color ?? '#FFB01F',
            } as CSSProperties
          }
        />
      ))}
    </div>
  );
}
